import { useMemo } from "react";
import { useStore } from "ra-core";

import type { DealStage, LabeledValue, NoteStatus } from "../types";
import { defaultConfiguration } from "./defaultConfiguration";

export const CONFIGURATION_STORE_KEY = "app.configuration";

export interface ConfigurationContextValue {
  companySectors: LabeledValue[];
  currency: string;
  dealCategories: LabeledValue[];
  dealPipelineStatuses: string[];
  dealStages: DealStage[];
  noteStatuses: NoteStatus[];
  taskTypes: LabeledValue[];
  title: string;
  darkModeLogo: string;
  lightModeLogo: string;
  /** the company's timezone (IANA name): its day decides when a task is overdue
   * and when notices go out (crm/db/15_outbox.sql) */
  timezone?: string;
  /** orders and payments from 1С (crm-api workers/onec.ts); the rest of its
   * settings is read by crm-api only */
  onec?: {
    enabled?: boolean;
    odataUrl?: string;
    dealKeyPrefix?: string;
    dealKeySuffix?: string;
  };
  /** 'own' (default): this CRM is the main one; 'bitrix': the data comes from
   * the client's Bitrix24 every 10 minutes and its records are read-only here */
  crmSource?: "own" | "bitrix";
  /** crm: false hides the CRM menu (leads, deals, clients, tasks) for a client
   * who keeps their own CRM; the data still syncs for the dashboard */
  modules?: { crm?: boolean };
}

export const useConfigurationContext = () => {
  const [config] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
    defaultConfiguration,
  );
  // Merge with defaults so that missing fields in stored config
  // fall back to default values (e.g. when new settings are added)
  return useMemo(() => ({ ...defaultConfiguration, ...config }), [config]);
};

export const useConfigurationUpdater = () => {
  const [, setConfig] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
  );
  return setConfig;
};
